'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from '@/lib/next-shim'

import { ConsultationStatus, GetConsultationQuery } from '@/core/graphql/types'
import { useGetConsultationLazyQuery } from '@/core/graphql/hooks'

const POLL_INTERVAL_MS = 30000

/**
 * Waiting-room controller: keeps the latest consultation, polls its status
 * every 30s, and redirects to the live session once the doctor makes it active
 * (either via the poll or the manual join button).
 */
export function useWaitingRoom(consultationId: string, initialConsultation: GetConsultationQuery['consultation']) {
  const router = useRouter()
  const [consultation, setConsultation] = useState(initialConsultation)
  const [isJoining, setIsJoining] = useState(false)

  const [getConsultation] = useGetConsultationLazyQuery({
    variables: { id: consultationId },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.consultation) {
        setConsultation(data.consultation)

        // If doctor is ready (consultation is active), redirect into the live session.
        if (data.consultation.status === ConsultationStatus.Active) {
          router.push(`/account/consultations/${consultationId}/live`)
        }
      }
    },
  })

  // Poll for status updates every 30 seconds.
  useEffect(() => {
    const interval = setInterval(() => {
      getConsultation()
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [getConsultation])

  const handleJoin = useCallback(() => {
    setIsJoining(true)
    router.push(`/account/consultations/${consultationId}/live`)
  }, [router, consultationId])

  const isReady = consultation.status === ConsultationStatus.Active

  return { consultation, isReady, isJoining, handleJoin }
}
